import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  Modal,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  useWindowDimensions,
  View,
} from "react-native";
import type { designTokens } from "@amplecare/design-tokens";
import type {
  ClinicianWorkbenchActionInput,
  ClinicianWorkbenchPatientRecordFieldMeta,
  ClinicianWorkbenchPatientRecordFieldPath,
  ClinicianWorkbenchPatientRecordV1,
  ClinicianWorkbenchQuestionnaireDefinitionV1,
} from "@amplecare/contracts";

import { LineIcon, type LineIconName } from "../line-icons.js";
import type { AppShellTheme } from "../shell-core.js";
import {
  workforceConfigurationDialog,
  workforceOperationalLayout,
  workforceOperationalTypography,
  workforceResponsiveBreakpoints,
} from "../workforce-design-system.js";
import {
  chartPackDocuments,
  computeChartPackDocumentGaps,
  renderChartPackDocumentHtml,
  renderFullChartPackHtml,
} from "./chart-pack/templates.js";
import {
  ClinicianQuestionnaireEntrySurface,
  type ClinicianQuestionnaireEntrySurfaceProps,
} from "./questionnaire-entry.js";
import { computeQuestionnaireRecordStatus } from "./questionnaire-entry-core.js";
import { resolveHorizontalTabNavigationIndex } from "./keyboard-navigation.js";
import {
  buildClinicianQuestionnaireEntryKey,
  formatClinicianOutboundSessionStatus,
  isClinicianLiveCallSessionStatus,
} from "./presentation.js";
import {
  formatCaseTime,
  formatCentsUsd,
  formatDateWithMonthName,
  formatPhoneDisplay,
  formatProvenanceTimestamp,
  formatYesNo,
} from "./record/format.js";
import { computeAgeYears } from "./record/scoring.js";
import {
  getPatientRecordValueByPath,
  setPatientRecordValueByPath,
} from "./record/record-core.js";
import {
  computePatientRecordReadiness,
  type PatientRecordSectionId,
} from "./record/readiness.js";
import {
  formatPhoneDraftAsTyped,
  parseAndValidateFieldDraft,
  validateCrossFieldRules,
  warningForFieldValue,
  type FieldKind,
  type ValidationIssue,
} from "./record/validation.js";

interface FieldDescriptor {
  path: ClinicianWorkbenchPatientRecordFieldPath;
  label: string;
  kind: FieldKind;
  options?: { value: string; label: string }[];
  placeholder?: string;
}

interface SectionDescriptor {
  id: PatientRecordSectionId;
  title: string;
  fields: FieldDescriptor[];
}

interface FieldGroupDescriptor {
  id: string;
  title: string;
  description: string;
}

type ProfileChoiceKeyDownEvent = {
  currentTarget?: unknown;
  key?: string;
  nativeEvent?: { key?: string };
  preventDefault?: () => void;
};

function handleProfileChoiceKeyDown(
  event: ProfileChoiceKeyDownEvent,
  currentIndex: number,
  optionValues: readonly string[],
  onSelect: (value: string) => void,
): void {
  const nextIndex = resolveHorizontalTabNavigationIndex(
    event.key ?? event.nativeEvent?.key ?? "",
    currentIndex,
    optionValues.length,
  );
  const nextValue = nextIndex == null ? undefined : optionValues[nextIndex];

  if (nextIndex == null || nextValue == null) {
    return;
  }

  event.preventDefault?.();
  onSelect(nextValue);

  const currentTarget = event.currentTarget as HTMLElement | null | undefined;
  const radioGroup = currentTarget?.closest?.('[role="radiogroup"]');

  setTimeout(() => {
    radioGroup
      ?.querySelectorAll<HTMLElement>('[role="radio"]')
      .item(nextIndex)
      ?.focus();
  }, 0);
}

const surgeryTypeOptions = [
  { value: "lower-extremity-arthroscopy", label: "Lower extremity arthroscopy" },
  { value: "lower-extremity-arthroplasty", label: "Lower extremity arthroplasty" },
  { value: "leg-fracture-surgery", label: "Fracture surgery of the leg" },
  { value: "upper-extremity-arthroscopy", label: "Upper extremity arthroscopy" },
  { value: "upper-extremity-arthroplasty", label: "Upper extremity arthroplasty" },
  { value: "upper-extremity-fracture-surgery", label: "Upper extremity fracture surgery" },
  { value: "laparoscopy", label: "Laparoscopy" },
  { value: "central-venous-access", label: "Placement of central venous access" },
  { value: "great-veins-of-leg", label: "Great veins of the leg" },
  { value: "other", label: "Other" },
];

const profileSections: SectionDescriptor[] = [
  {
    id: "patient",
    title: "Patient & contact",
    fields: [
      { path: "patient.firstName", label: "First name", kind: "text" },
      { path: "patient.lastName", label: "Last name", kind: "text" },
      { path: "patient.mrn", label: "MRN", kind: "text" },
      { path: "patient.dateOfBirth", label: "Date of birth", kind: "date" },
      {
        path: "patient.sex",
        label: "Sex",
        kind: "select",
        options: [
          { value: "male", label: "Male" },
          { value: "female", label: "Female" },
        ],
      },
      { path: "patient.addressStreet", label: "Street address", kind: "text" },
      { path: "patient.addressCity", label: "City", kind: "text" },
      { path: "patient.addressState", label: "State", kind: "state" },
      { path: "patient.addressZip", label: "Zip code", kind: "zip" },
      { path: "patient.heightInches", label: "Height (inches)", kind: "number" },
      { path: "patient.weightPounds", label: "Weight (pounds)", kind: "number" },
      { path: "patient.primaryPhone.number", label: "Primary phone (cell)", kind: "phone" },
      { path: "patient.secondaryPhone.number", label: "Secondary phone", kind: "phone" },
      { path: "patient.workPhone.number", label: "Work phone", kind: "phone" },
      { path: "patient.homePhone.number", label: "Home phone", kind: "phone" },
      { path: "patient.email", label: "Email", kind: "email" },
      { path: "patient.primaryLanguage", label: "Primary language", kind: "text" },
      { path: "patient.needsTranslator", label: "Needs translator", kind: "boolean" },
      { path: "patient.maritalStatus", label: "Marital status", kind: "text" },
      { path: "patient.race", label: "Race", kind: "text" },
      { path: "patient.ethnicity", label: "Ethnicity", kind: "text" },
      { path: "patient.religiousAffiliation", label: "Religious affiliation", kind: "text" },
      { path: "patient.legalGuardian", label: "Legal guardian", kind: "boolean" },
      { path: "patient.advanceDirective", label: "Advance directive", kind: "text" },
    ],
  },
  {
    id: "case",
    title: "Case",
    fields: [
      { path: "case.dateOfService", label: "Date of service", kind: "date" },
      { path: "case.caseTime", label: "Time of case", kind: "time" },
      { path: "case.surgeonName", label: "Physician (surgeon)", kind: "text" },
      {
        path: "case.procedures",
        label: "Procedure(s)",
        kind: "list",
        placeholder: "Separate multiple procedures with commas",
      },
      { path: "case.preopDiagnosis", label: "Pre-op diagnosis", kind: "text" },
      {
        path: "case.surgeryDurationMinutes",
        label: "Surgery duration (minutes, incl. anesthesia)",
        kind: "number",
      },
      {
        path: "case.surgeryTypeCategory",
        label: "Type of surgery (DVT category)",
        kind: "select",
        options: surgeryTypeOptions,
      },
    ],
  },
  {
    id: "contacts",
    title: "Contacts & care team",
    fields: [
      { path: "contacts.rideHome.name", label: "Ride home — name", kind: "text" },
      { path: "contacts.rideHome.relationship", label: "Ride home — relationship", kind: "text" },
      { path: "contacts.rideHome.phone", label: "Ride home — phone", kind: "phone" },
      { path: "contacts.caregiver.name", label: "Caregiver after surgery — name", kind: "text" },
      {
        path: "contacts.caregiver.relationship",
        label: "Caregiver after surgery — relationship",
        kind: "text",
      },
      { path: "contacts.caregiver.phone", label: "Caregiver after surgery — phone", kind: "phone" },
      { path: "contacts.emergencyContact1.name", label: "Emergency contact 1 — name", kind: "text" },
      {
        path: "contacts.emergencyContact1.relationship",
        label: "Emergency contact 1 — relationship",
        kind: "text",
      },
      { path: "contacts.emergencyContact1.phone", label: "Emergency contact 1 — phone", kind: "phone" },
      { path: "contacts.emergencyContact2.name", label: "Emergency contact 2 — name", kind: "text" },
      {
        path: "contacts.emergencyContact2.relationship",
        label: "Emergency contact 2 — relationship",
        kind: "text",
      },
      { path: "contacts.emergencyContact2.phone", label: "Emergency contact 2 — phone", kind: "phone" },
      { path: "contacts.primaryCarePhysician.name", label: "Primary care physician", kind: "text" },
      { path: "contacts.primaryCarePhysician.phone", label: "PCP phone", kind: "phone" },
      { path: "contacts.specialist1.name", label: "Specialist 1 — name", kind: "text" },
      { path: "contacts.specialist1.phone", label: "Specialist 1 — phone", kind: "phone" },
      { path: "contacts.specialist1.specialty", label: "Specialist 1 — specialty", kind: "text" },
      { path: "contacts.specialist2.name", label: "Specialist 2 — name", kind: "text" },
      { path: "contacts.specialist2.phone", label: "Specialist 2 — phone", kind: "phone" },
      { path: "contacts.specialist2.specialty", label: "Specialist 2 — specialty", kind: "text" },
      { path: "contacts.specialist3.name", label: "Specialist 3 — name", kind: "text" },
      { path: "contacts.specialist3.phone", label: "Specialist 3 — phone", kind: "phone" },
      { path: "contacts.specialist3.specialty", label: "Specialist 3 — specialty", kind: "text" },
      {
        path: "contacts.mayContactPhysicians",
        label: "May contact physician(s)",
        kind: "boolean",
      },
    ],
  },
  {
    id: "insurance",
    title: "Insurance & benefits",
    fields: [
      { path: "insurance.primaryPayer.companyName", label: "Primary payer", kind: "text" },
      { path: "insurance.primaryPayer.companyPhone", label: "Primary payer phone", kind: "phone" },
      { path: "insurance.primaryPayer.companyAddress", label: "Primary payer address", kind: "text" },
      { path: "insurance.primaryPayer.policyNumber", label: "Policy #", kind: "text" },
      { path: "insurance.primaryPayer.groupNumber", label: "Group #", kind: "text" },
      { path: "insurance.primaryPayer.policyHolderName", label: "Policy holder name", kind: "text" },
      {
        path: "insurance.primaryPayer.policyHolderRelationship",
        label: "Policy holder relationship",
        kind: "text",
      },
      {
        path: "insurance.primaryPayer.policyHolderAddress",
        label: "Policy holder address",
        kind: "text",
      },
      { path: "insurance.primaryPayer.policyHolderPhone", label: "Policy holder phone", kind: "phone" },
      { path: "insurance.primaryPayer.employerName", label: "Employer", kind: "text" },
      { path: "insurance.primaryPayer.employerPhone", label: "Employer phone", kind: "phone" },
      { path: "insurance.primaryPayer.employerAddress", label: "Employer address", kind: "text" },
      {
        path: "insurance.secondaryPayerStatus",
        label: "Secondary payer",
        kind: "select",
        options: [
          { value: "unknown", label: "Unknown" },
          { value: "none", label: "None" },
          { value: "present", label: "Present" },
        ],
      },
      { path: "insurance.secondaryPayer.companyName", label: "Secondary payer name", kind: "text" },
      { path: "insurance.secondaryPayer.policyNumber", label: "Secondary policy #", kind: "text" },
      { path: "insurance.secondaryPayer.groupNumber", label: "Secondary group #", kind: "text" },
      { path: "insurance.benefits.deductibleCents", label: "Deductible", kind: "money" },
      { path: "insurance.benefits.deductibleMetCents", label: "Deductible met", kind: "money" },
      { path: "insurance.benefits.outOfPocketMaxCents", label: "Out-of-pocket max", kind: "money" },
      { path: "insurance.benefits.outOfPocketMetCents", label: "Out-of-pocket met", kind: "money" },
      { path: "insurance.benefits.coInsurancePercent", label: "Co-insurance", kind: "percent" },
      { path: "insurance.benefits.coPaymentCents", label: "Co-payment", kind: "money" },
      {
        path: "insurance.benefits.patientResponsibilityCents",
        label: "Patient responsibility",
        kind: "money",
      },
    ],
  },
];

const profileNavigationTargetIds: ClinicianPatientProfileFocusTarget[] = [
  ...profileSections.map((section) => section.id),
  "chart-pack",
];

function findWebScrollAncestor(
  element: HTMLElement,
): HTMLElement | null {
  let candidate = element.parentElement;

  while (candidate) {
    const candidateStyle = window.getComputedStyle(candidate);

    if (/(auto|scroll)/u.test(candidateStyle.overflowY)) {
      return candidate;
    }

    candidate = candidate.parentElement;
  }

  return null;
}

const sectionIconNames: Record<PatientRecordSectionId, LineIconName> = {
  patient: "clinician",
  case: "appointment",
  contacts: "phone",
  insurance: "chart",
};

const secondaryPayerDetailPaths = new Set([
  "insurance.secondaryPayer.companyName",
  "insurance.secondaryPayer.policyNumber",
  "insurance.secondaryPayer.groupNumber",
]);

const fieldGroupsBySection: Record<
  PatientRecordSectionId,
  readonly FieldGroupDescriptor[]
> = {
  patient: [
    {
      id: "identity",
      title: "Identity",
      description: "Core identifiers and demographics",
    },
    {
      id: "address",
      title: "Address",
      description: "Primary residential address",
    },
    {
      id: "physical",
      title: "Physical details",
      description: "Measurements used in clinical preparation",
    },
    {
      id: "contact",
      title: "Contact",
      description: "Phone numbers and email",
    },
    {
      id: "preferences",
      title: "Preferences & directives",
      description: "Language, background, and care directives",
    },
  ],
  case: [
    {
      id: "schedule",
      title: "Schedule & clinician",
      description: "Date, time, and responsible physician",
    },
    {
      id: "procedure",
      title: "Procedure",
      description: "Procedure and pre-operative details",
    },
  ],
  contacts: [
    {
      id: "ride-home",
      title: "Ride home",
      description: "Discharge transport contact",
    },
    {
      id: "caregiver",
      title: "Post-operative caregiver",
      description: "Care support after surgery",
    },
    {
      id: "emergency",
      title: "Emergency contacts",
      description: "Primary and alternate emergency contacts",
    },
    {
      id: "care-team",
      title: "Care team",
      description: "Primary care and specialist contacts",
    },
    {
      id: "permissions",
      title: "Contact permissions",
      description: "Authorization to coordinate care",
    },
  ],
  insurance: [
    {
      id: "primary",
      title: "Primary coverage",
      description: "Payer and policy identifiers",
    },
    {
      id: "holder",
      title: "Policy holder & employer",
      description: "Subscriber and employment details",
    },
    {
      id: "secondary",
      title: "Secondary coverage",
      description: "Additional payer information",
    },
    {
      id: "benefits",
      title: "Benefits & responsibility",
      description: "Deductible, payment, and coverage amounts",
    },
  ],
};

function resolveFieldGroupId(
  sectionId: PatientRecordSectionId,
  fieldPath: ClinicianWorkbenchPatientRecordFieldPath,
): string {
  if (sectionId === "patient") {
    if (
      fieldPath === "patient.firstName" ||
      fieldPath === "patient.lastName" ||
      fieldPath === "patient.mrn" ||
      fieldPath === "patient.dateOfBirth" ||
      fieldPath === "patient.sex"
    ) {
      return "identity";
    }

    if (fieldPath.includes("address")) {
      return "address";
    }

    if (
      fieldPath === "patient.heightInches" ||
      fieldPath === "patient.weightPounds"
    ) {
      return "physical";
    }

    if (fieldPath.includes("Phone") || fieldPath === "patient.email") {
      return "contact";
    }

    return "preferences";
  }

  if (sectionId === "case") {
    return fieldPath === "case.dateOfService" ||
      fieldPath === "case.caseTime" ||
      fieldPath === "case.surgeonName"
      ? "schedule"
      : "procedure";
  }

  if (sectionId === "contacts") {
    if (fieldPath.startsWith("contacts.rideHome.")) {
      return "ride-home";
    }

    if (fieldPath.startsWith("contacts.caregiver.")) {
      return "caregiver";
    }

    if (fieldPath.startsWith("contacts.emergencyContact")) {
      return "emergency";
    }

    if (fieldPath === "contacts.mayContactPhysicians") {
      return "permissions";
    }

    return "care-team";
  }

  if (fieldPath.startsWith("insurance.benefits.")) {
    return "benefits";
  }

  if (
    fieldPath === "insurance.secondaryPayerStatus" ||
    fieldPath.startsWith("insurance.secondaryPayer.")
  ) {
    return "secondary";
  }

  if (
    fieldPath.includes("policyHolder") ||
    fieldPath.includes("employer")
  ) {
    return "holder";
  }

  return "primary";
}

function shouldFieldSpanFullWidth(
  field: FieldDescriptor,
): boolean {
  return (
    field.kind === "list" ||
    field.path === "patient.addressStreet" ||
    field.path === "patient.advanceDirective" ||
    field.path === "case.preopDiagnosis" ||
    field.path.endsWith("Address")
  );
}

export function formatPatientRecordFieldValue(
  record: ClinicianWorkbenchPatientRecordV1,
  field: FieldDescriptor,
): string | null {
  const value = getPatientRecordValueByPath(record, field.path);

  if (value == null) {
    return null;
  }

  switch (field.kind) {
    case "date":
      return formatDateWithMonthName(value as string);
    case "money":
      return formatCentsUsd(value as number);
    case "percent":
      return `${String(value)}%`;
    case "phone":
      return formatPhoneDisplay(value as string);
    case "boolean":
      return formatYesNo(value as boolean);
    case "list": {
      const items = value as string[];

      return items.length > 0 ? items.join(", ") : null;
    }
    case "select": {
      const option = field.options?.find(
        (candidate) => candidate.value === value,
      );

      if (field.path === "insurance.secondaryPayerStatus" && value === "unknown") {
        return null;
      }

      return option?.label ?? String(value);
    }
    default: {
      const text = String(value).trim();

      return text.length > 0 ? text : null;
    }
  }
}

function fieldValueToDraftText(
  record: ClinicianWorkbenchPatientRecordV1,
  field: FieldDescriptor,
): string {
  const value = getPatientRecordValueByPath(record, field.path);

  if (value == null) {
    return "";
  }

  switch (field.kind) {
    case "money":
      return String((value as number) / 100);
    case "list":
      return (value as string[]).join(", ");
    case "boolean":
    case "select":
      return String(value);
    case "phone":
      return formatPhoneDisplay(value as string) ?? String(value);
    case "date":
      // Drafts follow the display convention (and the input placeholder),
      // not the ISO storage format.
      return formatDateWithMonthName(value as string) ?? String(value);
    default:
      return String(value);
  }
}


function resolveFieldMeta(
  record: ClinicianWorkbenchPatientRecordV1,
  fieldPath: ClinicianWorkbenchPatientRecordFieldPath,
): {
  metaPath: ClinicianWorkbenchPatientRecordFieldPath;
  meta: ClinicianWorkbenchPatientRecordFieldMeta;
} | null {
  const segments = fieldPath.split(".");

  for (let length = segments.length; length > 0; length -= 1) {
    const candidate = segments.slice(0, length).join(".");
    const meta = record.fieldMeta[candidate];

    if (meta) {
      return { metaPath: candidate, meta };
    }
  }

  return null;
}

const sourceLabels: Record<string, string> = {
  manual: "Manual",
  questionnaire: "Questionnaire",
  hst_import: "HST",
  passport_import: "Passport",
  legacy_migration: "Migrated",
};

export function buildPatientRecordSectionCopyText(
  record: ClinicianWorkbenchPatientRecordV1,
  section: SectionDescriptor,
): string {
  const lines = [section.title.toUpperCase()];

  for (const field of section.fields) {
    lines.push(
      `${field.label}: ${formatPatientRecordFieldValue(record, field) ?? "Not captured"}`,
    );
  }

  return lines.join("\n");
}

export type ClinicianPatientProfileFocusTarget =
  | PatientRecordSectionId
  | "chart-pack";

export interface ClinicianPatientProfileSurfaceProps {
  actionFeedbackText: string | null;
  actionFeedbackTone: "error" | "pending" | "success" | null;
  discardChangesRequestKey: number;
  discardConfirmationDismissRequestKey: number;
  focusSection: ClinicianPatientProfileFocusTarget | null;
  isOutboundSessionLoading: boolean;
  isWorkbenchActionSubmitting: boolean;
  layout: (typeof designTokens)["semanticLayout"];
  materialChangeToken: string;
  onDiscardConfirmationOpenChange: (open: boolean) => void;
  onDirtyStateChange: (dirty: boolean) => void;
  onFocusSectionHandled: () => void;
  onModalOpenChange: (open: boolean) => void;
  onSubmitWorkbenchAction: (
    action: ClinicianWorkbenchActionInput,
    options?: { onSuccess?: () => void; onError?: (message: string) => void },
  ) => void;
  navigationConfirmationOpen: boolean;
  patientDisplayName: string;
  patientId: string;
  patientIdentityText: string | null;
  /** Tenant-served questionnaire definition; null when the tenant has not
   *  enabled clinician questionnaire entry (card and surface stay hidden). */
  questionnaireDefinition: ClinicianWorkbenchQuestionnaireDefinitionV1 | null;
  record: ClinicianWorkbenchPatientRecordV1;
  theme: AppShellTheme;
  tokens: typeof designTokens;
  /** Active AI-call session (unfinished) for phone-answer prefill + manual
   *  completion inside the questionnaire entry surface. */
  outboundSession?: ClinicianQuestionnaireEntrySurfaceProps["outboundSession"];
  outboundSessionStatus?: string | null;
  outboundAnswerPreview?: ClinicianQuestionnaireEntrySurfaceProps["outboundAnswerPreview"];
  onSubmitOutboundManualCommand?: ClinicianQuestionnaireEntrySurfaceProps["onSubmitOutboundManualCommand"];
  isOutboundCommandSubmitting?: boolean;
}

export function ClinicianPatientProfileSurface({
  actionFeedbackText,
  actionFeedbackTone,
  discardChangesRequestKey,
  discardConfirmationDismissRequestKey,
  focusSection,
  isOutboundSessionLoading,
  isWorkbenchActionSubmitting,
  layout,
  materialChangeToken,
  onDiscardConfirmationOpenChange,
  onDirtyStateChange,
  onFocusSectionHandled,
  onModalOpenChange,
  onSubmitWorkbenchAction,
  navigationConfirmationOpen,
  patientDisplayName,
  patientId,
  patientIdentityText,
  questionnaireDefinition,
  record,
  theme,
  tokens,
  outboundSession,
  outboundSessionStatus,
  outboundAnswerPreview,
  onSubmitOutboundManualCommand,
  isOutboundCommandSubmitting,
}: ClinicianPatientProfileSurfaceProps) {
  const { width: viewportWidth } = useWindowDimensions();
  const isCompact =
    viewportWidth < workforceResponsiveBreakpoints.compact;
  const usesInlineEditActions =
    viewportWidth < workforceResponsiveBreakpoints.wide;
  const usesTwoColumnFields =
    viewportWidth >= workforceResponsiveBreakpoints.medium;
  const controlMinHeight =
    Platform.OS === "web" && !usesInlineEditActions
      ? workforceConfigurationDialog.controlHeight
      : workforceOperationalLayout.mobileControlHeight;
  const displayRecord = useMemo(() => record, [record]);
  const readiness = computePatientRecordReadiness(record);
  const questionnaireStatus = useMemo(
    () =>
      questionnaireDefinition == null
        ? null
        : computeQuestionnaireRecordStatus({
            questions: questionnaireDefinition.questions,
            bindings: questionnaireDefinition.chartBindings,
            getFieldValue: (fieldPath) =>
              getPatientRecordValueByPath(displayRecord, fieldPath),
            resolveMeta: (fieldPath) =>
              resolveFieldMeta(
                displayRecord,
                fieldPath as ClinicianWorkbenchPatientRecordFieldPath,
              )?.meta ?? null,
            context: {
              ageYears: computeAgeYears(
                displayRecord.patient.dateOfBirth,
                displayRecord.case.dateOfService,
              ),
              sexAtBirth: displayRecord.patient.sex,
            },
          }),
    [questionnaireDefinition, displayRecord],
  );
  // Chart-pack renderers take the tenant-served questionnaire content when the
  // definition is present; undefined keeps their bundled-fixture fallback.
  const chartPackQuestionnaireContent = questionnaireDefinition
    ? {
        questions: questionnaireDefinition.questions,
        chartBindings: questionnaireDefinition.chartBindings,
      }
    : undefined;
  const [showQuestionnaireEntry, setShowQuestionnaireEntry] = useState(false);
  const [questionnaireDirty, setQuestionnaireDirty] = useState(false);
  const [questionnaireEntryGeneration, setQuestionnaireEntryGeneration] =
    useState(0);
  const [questionnaireEntryContext, setQuestionnaireEntryContext] = useState<{
    answerPreview:
      | ClinicianQuestionnaireEntrySurfaceProps["outboundAnswerPreview"]
      | undefined;
    session:
      | ClinicianQuestionnaireEntrySurfaceProps["outboundSession"]
      | undefined;
  }>({
    answerPreview: undefined,
    session: undefined,
  });
  const [editingSectionId, setEditingSectionId] =
    useState<PatientRecordSectionId | null>(null);
  const [showProfileDiscardConfirmation, setShowProfileDiscardConfirmation] =
    useState(false);
  const [draftTexts, setDraftTexts] = useState<Record<string, string>>({});
  const [copiedSectionId, setCopiedSectionId] = useState<string | null>(null);
  const [utilityFeedback, setUtilityFeedback] = useState<{
    text: string;
    tone: "error" | "success";
  } | null>(null);
  const [blurredDraftPaths, setBlurredDraftPaths] = useState<
    Record<string, boolean>
  >({});
  const [hasSubmittedPatientRecordAction, setHasSubmittedPatientRecordAction] =
    useState(false);
  const [previewTarget, setPreviewTarget] = useState<
    { kind: "doc"; index: number } | { kind: "pack" } | null
  >(null);
  const [activeSectionId, setActiveSectionId] =
    useState<ClinicianPatientProfileFocusTarget>("patient");
  const scrollRef = useRef<ScrollView | null>(null);
  const navigationScrollRef = useRef<ScrollView | null>(null);
  const navigationOffsets = useRef<
    Record<string, { width: number; x: number }>
  >({});
  const navigationScrollLeft = useRef(0);
  const navigationViewportWidth = useRef(0);
  const profileDiscardStayButtonRef = useRef<View | null>(null);
  const sectionOffsets = useRef<Record<string, number>>({});
  const stickyHeaderHeight = useRef(0);
  const revealProfileNavigationTarget = useCallback(
    (
      target: ClinicianPatientProfileFocusTarget,
      animated: boolean,
    ): void => {
      if (!usesInlineEditActions) {
        return;
      }

      const item = navigationOffsets.current[target];
      const viewportWidth = navigationViewportWidth.current;

      if (!item || viewportWidth <= 0) {
        return;
      }

      const currentLeft = navigationScrollLeft.current;
      const currentRight = currentLeft + viewportWidth;
      let nextLeft = currentLeft;

      if (item.x < currentLeft + 8) {
        nextLeft = Math.max(0, item.x - 8);
      } else if (item.x + item.width > currentRight - 8) {
        nextLeft = Math.max(0, item.x + item.width - viewportWidth + 8);
      }

      if (nextLeft !== currentLeft) {
        navigationScrollRef.current?.scrollTo({
          animated,
          x: nextLeft,
        });
      }
    },
    [usesInlineEditActions],
  );
  const scrollProfileToTarget = useCallback(
    (
      target: ClinicianPatientProfileFocusTarget,
      animated: boolean,
    ): void => {
      if (typeof document !== "undefined" && typeof window !== "undefined") {
        const targetElement = document.getElementById(
          `clinician-profile-section-${target}`,
        );

        if (targetElement?.getBoundingClientRect) {
          const scrollParent = findWebScrollAncestor(targetElement);

          if (scrollParent) {
            const targetRect = targetElement.getBoundingClientRect();
            const parentRect = scrollParent.getBoundingClientRect();
            const nextScrollTop =
              scrollParent.scrollTop +
              targetRect.top -
              parentRect.top -
              stickyHeaderHeight.current -
              8;

            scrollParent.scrollTop = Math.max(0, nextScrollTop);
            return;
          }
        }
      }

      const offset = sectionOffsets.current[target];

      if (offset != null) {
        scrollRef.current?.scrollTo({
          y: Math.max(0, offset - stickyHeaderHeight.current - 8),
          animated,
        });
      }
    },
    [],
  );

  const editingSection =
    profileSections.find((section) => section.id === editingSectionId) ?? null;
  // Validation is derived state, recomputed from the drafts on every render —
  // no second source of truth to keep in sync with the inputs.
  const draftValidation = useMemo(() => {
    if (!editingSection) {
      return null;
    }

    const parsedByPath: Record<
      string,
      ReturnType<typeof parseAndValidateFieldDraft>
    > = {};
    const changedByPath: Record<string, boolean> = {};
    const errors: ValidationIssue[] = [];
    const warnings: ValidationIssue[] = [];
    const previewRecord = structuredClone(displayRecord);

    for (const field of editingSection.fields) {
      const draftText = draftTexts[field.path] ?? "";
      const isChanged = draftText !== fieldValueToDraftText(displayRecord, field);
      const result = parseAndValidateFieldDraft(
        field,
        draftText,
      );

      parsedByPath[field.path] = result;
      changedByPath[field.path] = isChanged;

      if (!result.ok) {
        if (isChanged) {
          errors.push(result.issue);
        }
        continue;
      }

      setPatientRecordValueByPath(
        previewRecord as unknown as Record<string, unknown>,
        field.path,
        result.value,
      );

      const warning = warningForFieldValue(field, result.value);

      if (warning && isChanged) {
        warnings.push(warning);
      }
    }

    if (Object.values(changedByPath).some(Boolean)) {
      const editingFieldPaths = new Set(
        editingSection.fields.map((field) => field.path),
      );
      const todayIso = new Date().toISOString().slice(0, 10);

      for (const crossIssue of validateCrossFieldRules(previewRecord, todayIso)) {
        if (editingFieldPaths.has(crossIssue.fieldPath)) {
          (crossIssue.severity === "error" ? errors : warnings).push(crossIssue);
        }
      }
    }

    return { changedByPath, errors, parsedByPath, warnings };
  }, [editingSection, draftTexts, displayRecord]);
  const changedFieldCount = draftValidation
    ? Object.values(draftValidation.changedByPath).filter(Boolean).length
    : 0;

  useEffect(() => {
    onDirtyStateChange(changedFieldCount > 0 || questionnaireDirty);
  }, [changedFieldCount, onDirtyStateChange, questionnaireDirty]);

  useEffect(() => {
    setCopiedSectionId(null);
    setUtilityFeedback(null);
  }, [patientId]);

  useEffect(() => {
    if (previewTarget != null) {
      setUtilityFeedback(null);
    }
  }, [previewTarget?.kind, previewTarget?.kind === "doc" ? previewTarget.index : null]);

  useEffect(
    () => () => {
      onDirtyStateChange(false);
    },
    [onDirtyStateChange],
  );

  useEffect(() => {
    if (!showProfileDiscardConfirmation) {
      return;
    }

    const focusTimeout = setTimeout(() => {
      const stayButton =
        profileDiscardStayButtonRef.current as unknown as {
          focus?: () => void;
        } | null;

      stayButton?.focus?.();
    }, 0);

    return () => clearTimeout(focusTimeout);
  }, [showProfileDiscardConfirmation]);

  useEffect(() => {
    setShowProfileDiscardConfirmation(false);
  }, [discardConfirmationDismissRequestKey]);

  useEffect(() => {
    if (discardChangesRequestKey === 0) {
      return;
    }

    setShowProfileDiscardConfirmation(false);
    setQuestionnaireDirty(false);
    setShowQuestionnaireEntry(false);
    setQuestionnaireEntryGeneration((value) => value + 1);
    setQuestionnaireEntryContext({
      answerPreview: undefined,
      session: undefined,
    });
    setEditingSectionId(null);
    setDraftTexts({});
    setBlurredDraftPaths({});
    setHasSubmittedPatientRecordAction(false);
    setPreviewTarget(null);
  }, [discardChangesRequestKey]);

  useEffect(() => {
    onDiscardConfirmationOpenChange(
      showProfileDiscardConfirmation && !navigationConfirmationOpen,
    );

    return () => onDiscardConfirmationOpenChange(false);
  }, [
    navigationConfirmationOpen,
    onDiscardConfirmationOpenChange,
    showProfileDiscardConfirmation,
  ]);

  useEffect(() => {
    const isOpen =
      !navigationConfirmationOpen &&
      (showQuestionnaireEntry ||
        previewTarget != null ||
        showProfileDiscardConfirmation);

    onModalOpenChange(isOpen);

    return () => onModalOpenChange(false);
  }, [
    navigationConfirmationOpen,
    onModalOpenChange,
    previewTarget,
    showProfileDiscardConfirmation,
    showQuestionnaireEntry,
  ]);

  useEffect(() => {
    if (!focusSection) {
      return;
    }

    if (focusSection === "chart-pack" && editingSectionId == null) {
      setPreviewTarget({ kind: "pack" });
    }

    setActiveSectionId(focusSection);

    const focusTimer = setTimeout(() => {
      scrollProfileToTarget(focusSection, true);
      revealProfileNavigationTarget(focusSection, true);
      // Focus requests are one-shot: clearing lets the same target fire again
      // on the next request and keeps remounts from replaying the last one.
      onFocusSectionHandled();
    }, 0);

    return () => clearTimeout(focusTimer);
  }, [
    editingSectionId,
    focusSection,
    onFocusSectionHandled,
    revealProfileNavigationTarget,
    scrollProfileToTarget,
  ]);

  const beginEditingSection = (section: SectionDescriptor) => {
    if (editingSectionId != null && editingSectionId !== section.id) {
      return;
    }

    const nextDrafts: Record<string, string> = {};

    for (const field of section.fields) {
      nextDrafts[field.path] = fieldValueToDraftText(displayRecord, field);
    }

    setDraftTexts(nextDrafts);
    setBlurredDraftPaths({});
    setHasSubmittedPatientRecordAction(false);
    setEditingSectionId(section.id);
  };

  const cancelEditingSection = () => {
    if (changedFieldCount > 0) {
      setShowProfileDiscardConfirmation(true);
      return;
    }

    discardEditingSection();
  };

  const discardEditingSection = () => {
    setShowProfileDiscardConfirmation(false);
    setHasSubmittedPatientRecordAction(false);
    setEditingSectionId(null);
    setDraftTexts({});
    setBlurredDraftPaths({});
  };

  // A live AI call owns the questionnaire: the patient is answering it on the
  // phone right now, so a clinician edit would race their answers into the same
  // session. Paused sessions stay editable (manual continuation).
  const isQuestionnaireLockedByLiveCall =
    isClinicianLiveCallSessionStatus(outboundSessionStatus);

  const openQuestionnaireEntry = () => {
    if (
      isQuestionnaireLockedByLiveCall ||
      isOutboundSessionLoading ||
      editingSectionId != null
    ) {
      return;
    }

    setQuestionnaireEntryContext({
      answerPreview: outboundAnswerPreview,
      session: outboundSession,
    });
    setShowQuestionnaireEntry(true);
  };

  const scrollToProfileSection = (
    target: ClinicianPatientProfileFocusTarget,
  ) => {
    setActiveSectionId(target);
    revealProfileNavigationTarget(target, true);
    scrollProfileToTarget(target, true);
  };

  const saveEditingSection = (section: SectionDescriptor) => {
    if (!draftValidation || draftValidation.errors.length > 0) {
      // Unreachable from the UI (Save is disabled), kept as a hard guard so
      // no caller can reintroduce the old silent-skip behavior.
      return;
    }

    const partial: Record<string, unknown> = {};
    const touchedFieldPaths: ClinicianWorkbenchPatientRecordFieldPath[] = [];

    for (const field of section.fields) {
      const draftText = draftTexts[field.path] ?? "";
      const previousText = fieldValueToDraftText(displayRecord, field);

      if (draftText === previousText) {
        continue;
      }

      const result = draftValidation.parsedByPath[field.path];

      if (!result || !result.ok) {
        return;
      }

      setPatientRecordValueByPath(partial, field.path, result.value);
      touchedFieldPaths.push(field.path);
    }

    if (touchedFieldPaths.length > 0) {
      setHasSubmittedPatientRecordAction(true);
      onSubmitWorkbenchAction(
        {
          kind: "upsert-patient-record",
          patientId,
          expectedMaterialChangeToken: materialChangeToken,
          record: partial as never,
          touchedFieldPaths,
        },
        {
          onSuccess: () => {
            setEditingSectionId(null);
            setDraftTexts({});
            setBlurredDraftPaths({});
          },
        },
      );
      return;
    }

    setEditingSectionId(null);
    setDraftTexts({});
    setBlurredDraftPaths({});
  };

  const confirmSuggestedField = (
    metaPath: ClinicianWorkbenchPatientRecordFieldPath,
  ) => {
    setHasSubmittedPatientRecordAction(true);
    onSubmitWorkbenchAction({
      kind: "upsert-patient-record",
      patientId,
      expectedMaterialChangeToken: materialChangeToken,
      record: {},
      touchedFieldPaths: [],
      reviewConfirmations: [metaPath],
    });
  };

  const writeProfileClipboard = async (
    valueText: string,
    copiedId: string,
  ): Promise<void> => {
    const clipboard =
      Platform.OS === "web" && typeof navigator !== "undefined"
        ? navigator.clipboard
        : undefined;

    if (!clipboard?.writeText) {
      setUtilityFeedback({
        text: "Copy is unavailable in this browser.",
        tone: "error",
      });
      return;
    }

    try {
      await clipboard.writeText(valueText);
      setCopiedSectionId(copiedId);
      setUtilityFeedback({
        text: "Patient details copied to the clipboard.",
        tone: "success",
      });
      setTimeout(() => setCopiedSectionId(null), 1600);
    } catch {
      setUtilityFeedback({
        text: "Patient details could not be copied. Check browser permissions and try again.",
        tone: "error",
      });
    }
  };

  const copySectionText = (section: SectionDescriptor) => {
    void writeProfileClipboard(
      buildPatientRecordSectionCopyText(displayRecord, section),
      section.id,
    );
  };

  const copyWholeProfile = () => {
    void writeProfileClipboard(
        profileSections
          .map((section) =>
            buildPatientRecordSectionCopyText(displayRecord, section),
          )
          .join("\n\n"),
      "all",
    );
  };

  const renderActionPill = (input: {
    accessibilityHint?: string;
    accessibilityLabel?: string;
    disabled?: boolean;
    emphasis?: "outline" | "solid";
    iconName?: LineIconName;
    key?: string;
    label: string;
    onPress: () => void;
    tone?: "accent" | "neutral" | "warning";
  }) => {
    const toneColor =
      input.tone === "warning"
        ? theme.warning
        : input.tone === "neutral"
          ? theme.textSecondary
          : theme.accentStrong;
    const isSolid = input.emphasis === "solid";

    return (
      <Pressable
        accessibilityHint={input.accessibilityHint}
        accessibilityLabel={input.accessibilityLabel ?? input.label}
        accessibilityRole="button"
        accessibilityState={{ disabled: input.disabled === true }}
        aria-disabled={input.disabled === true}
        disabled={input.disabled}
        key={input.key ?? input.label}
        onPress={input.onPress}
        style={({ pressed }) => ({
          alignItems: "center",
          backgroundColor: input.disabled
            ? theme.backgroundSubtle
            : isSolid
              ? toneColor
              : pressed
                ? theme.backgroundSubtle
                : theme.surface,
          borderColor: input.disabled ? theme.borderSubtle : toneColor,
          borderRadius: workforceOperationalLayout.controlRadius,
          borderWidth: 1,
          flexDirection: "row",
          gap: 6,
          justifyContent: "center",
          minHeight: controlMinHeight,
          opacity: input.disabled ? 0.55 : 1,
          paddingHorizontal: 12,
          paddingVertical: 6,
        })}
      >
        {input.iconName ? (
          <LineIcon
            color={
              input.disabled
                ? theme.textSecondary
                : isSolid
                  ? theme.accentContrast
                  : toneColor
            }
            name={input.iconName}
            size={15}
            strokeWidth={2.2}
          />
        ) : null}
        <Text
          style={{
            color: input.disabled
              ? theme.textSecondary
              : isSolid
                ? theme.accentContrast
                : toneColor,
            fontFamily: layout.bodyFontFamily,
            fontSize: workforceOperationalTypography.table.fontSize,
            fontWeight: "700",
            lineHeight: workforceOperationalTypography.table.lineHeight,
          }}
        >
          {input.label}
        </Text>
      </Pressable>
    );
  };

  const renderEditingActions = (section: SectionDescriptor) => {
    const sectionErrorCount = draftValidation?.errors.length ?? 0;

    return (
      <>
        {renderActionPill({
          accessibilityLabel: `Cancel editing ${section.title}`,
          disabled: isWorkbenchActionSubmitting,
          iconName: "x",
          label: "Cancel",
          onPress: cancelEditingSection,
          tone: "neutral",
        })}
        {renderActionPill({
          accessibilityLabel: `Save ${section.title}`,
          disabled: isWorkbenchActionSubmitting || sectionErrorCount > 0,
          emphasis: "solid",
          iconName: "check",
          label:
            sectionErrorCount > 0
              ? `Fix ${String(sectionErrorCount)} field${
                  sectionErrorCount === 1 ? "" : "s"
                }`
              : isWorkbenchActionSubmitting
                ? "Saving"
                : "Save changes",
          onPress: () => saveEditingSection(section),
        })}
      </>
    );
  };

  const renderProvenanceChip = (
    field: FieldDescriptor,
  ) => {
    const resolved = resolveFieldMeta(displayRecord, field.path);

    if (!resolved || resolved.meta.source === "manual") {
      return null;
    }

    const isSuggested = resolved.meta.reviewState === "suggested";
    const chipColor = isSuggested ? theme.warning : theme.textTertiary;

    return (
      <View
        style={{
          alignItems: "center",
          flexDirection: "row",
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <View
          style={{
            borderColor: chipColor,
            borderRadius: workforceOperationalLayout.controlRadius,
            borderWidth: 1,
            paddingHorizontal: 7,
            paddingVertical: 3,
          }}
        >
          <Text
            style={{
              color: chipColor,
              fontFamily: layout.bodyFontFamily,
              fontSize: workforceOperationalTypography.metadata.fontSize,
              fontWeight: "700",
              letterSpacing: 0,
              lineHeight: workforceOperationalTypography.metadata.lineHeight,
              textTransform: "uppercase",
            }}
          >
            {`${sourceLabels[resolved.meta.source] ?? resolved.meta.source}${isSuggested ? " · to review" : ""}`}
          </Text>
        </View>
        {isSuggested
          ? renderActionPill({
              accessibilityLabel: `Confirm suggested ${field.label}`,
              disabled: isWorkbenchActionSubmitting,
              iconName: "check",
              label: "Confirm",
              onPress: () => confirmSuggestedField(resolved.metaPath),
              tone: "warning",
            })
          : null}
      </View>
    );
  };

  const renderFieldRow = (
    section: SectionDescriptor,
    group: FieldGroupDescriptor,
    field: FieldDescriptor,
  ) => {
    if (
      secondaryPayerDetailPaths.has(field.path) &&
      displayRecord.insurance.secondaryPayerStatus !== "present" &&
      editingSectionId !== section.id
    ) {
      return null;
    }

    const isEditing = editingSectionId === section.id;
    const valueText = formatPatientRecordFieldValue(displayRecord, field);
    const parsedDraft = isEditing
      ? draftValidation?.parsedByPath[field.path]
      : undefined;
    const draftText = draftTexts[field.path] ?? "";
    const isChanged = draftValidation?.changedByPath[field.path] === true;
    const isTouched = blurredDraftPaths[field.path] === true || isChanged;
    const errorIssue =
      isEditing && parsedDraft && !parsedDraft.ok && isChanged && isTouched
        ? parsedDraft.issue
        : null;
    const warningIssue =
      isEditing && errorIssue == null
        ? (draftValidation?.warnings.find(
            (candidate) => candidate.fieldPath === field.path,
          ) ?? null)
        : null;
    const normalizedDraftDisplay =
      isEditing &&
      parsedDraft?.ok === true &&
      parsedDraft.value != null &&
      draftText.trim().length > 0 &&
      (field.kind === "date" || field.kind === "time")
        ? field.kind === "date"
          ? formatDateWithMonthName(parsedDraft.value as string)
          : formatCaseTime(parsedDraft.value as string)
        : null;
    // Only echo the normalized form when it tells the user something new
    // (e.g. "5/10/65" → "5/10/1965") — not when it restates their input.
    const normalizedEcho =
      normalizedDraftDisplay != null && normalizedDraftDisplay !== draftText.trim()
        ? normalizedDraftDisplay
        : null;
    const editBorderColor = errorIssue
      ? theme.danger
      : warningIssue
        ? theme.warning
        : theme.borderStrong;
    const keyboardTypeForKind =
      field.kind === "email"
        ? ("email-address" as const)
        : field.kind === "phone"
          ? ("phone-pad" as const)
          : field.kind === "number" ||
              field.kind === "money" ||
              field.kind === "percent" ||
              field.kind === "zip"
            ? ("numeric" as const)
            : ("default" as const);
    const applyDraftText = (text: string) => {
      const nextText =
        field.kind === "phone"
          ? formatPhoneDraftAsTyped(text)
          : field.kind === "state"
            ? text.toUpperCase()
            : text;

      setDraftTexts((previous) => ({ ...previous, [field.path]: nextText }));
    };
    const contextualFieldLabel =
      `${section.title}, ${group.title}, ${field.label}`;
    const spansFullWidth = shouldFieldSpanFullWidth(field);
    const choiceOptions =
      field.kind === "boolean"
        ? [
            { value: "true", label: "Yes" },
            { value: "false", label: "No" },
          ]
        : (field.options ?? []);

    return (
      <View
        key={field.path}
        style={{
          borderBottomColor: theme.borderSubtle,
          borderBottomWidth: 1,
          gap: workforceConfigurationDialog.fieldLabelGap,
          minHeight: isEditing ? controlMinHeight + 38 : 68,
          paddingHorizontal: 2,
          paddingVertical: 10,
          width:
            usesTwoColumnFields && !spansFullWidth
              ? "48.5%"
              : "100%",
        }}
      >
        <Text
          style={{
            color: theme.textSecondary,
            fontFamily: layout.bodyFontFamily,
            fontSize: workforceOperationalTypography.metadata.fontSize,
            fontWeight: "700",
            lineHeight: workforceOperationalTypography.metadata.lineHeight,
          }}
        >
          {field.label}
        </Text>
        <View style={{ gap: 6, minWidth: 0 }}>
          {isEditing ? (
            field.kind === "boolean" || field.kind === "select" ? (
              <View
                accessibilityLabel={`${contextualFieldLabel} options`}
                role="radiogroup"
                style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}
              >
                {choiceOptions.map((option, optionIndex) => {
                  const isSelected = draftTexts[field.path] === option.value;

                  return (
                    <Pressable
                      accessibilityLabel={`${contextualFieldLabel}: ${option.label}`}
                      accessibilityRole="radio"
                      accessibilityState={{ checked: isSelected }}
                      aria-checked={isSelected}
                      key={option.value}
                      onPress={() => {
                        setDraftTexts((previous) => ({
                          ...previous,
                          [field.path]: option.value,
                        }));
                      }}
                      tabIndex={
                        isSelected ||
                        (draftTexts[field.path]?.length === 0 &&
                          optionIndex === 0)
                          ? 0
                          : -1
                      }
                      {...(Platform.OS === "web"
                        ? {
                            onKeyDown: (event: unknown) =>
                              handleProfileChoiceKeyDown(
                                event as ProfileChoiceKeyDownEvent,
                                optionIndex,
                                choiceOptions.map(
                                  (candidate) => candidate.value,
                                ),
                                (nextValue) =>
                                  setDraftTexts((previous) => ({
                                    ...previous,
                                    [field.path]: nextValue,
                                  })),
                              ),
                          }
                        : {})}
                      style={{
                        alignItems: "center",
                        backgroundColor: isSelected
                          ? theme.accent
                          : theme.surface,
                        borderColor: isSelected
                          ? theme.accent
                          : theme.borderStrong,
                        borderRadius: workforceOperationalLayout.controlRadius,
                        borderWidth: 1,
                        justifyContent: "center",
                        minHeight: controlMinHeight,
                        paddingHorizontal: 12,
                        paddingVertical: 6,
                      }}
                    >
                      <Text
                        style={{
                          color: isSelected
                            ? theme.accentContrast
                            : theme.textSecondary,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: workforceOperationalTypography.table.fontSize,
                          fontWeight: "700",
                          lineHeight: workforceOperationalTypography.table.lineHeight,
                        }}
                      >
                        {option.label}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>
            ) : (
              <TextInput
                accessibilityHint={
                  errorIssue?.message ?? warningIssue?.message ?? normalizedEcho ?? undefined
                }
                accessibilityLabel={contextualFieldLabel}
                aria-invalid={errorIssue != null}
                keyboardType={keyboardTypeForKind}
                onBlur={() =>
                  setBlurredDraftPaths((previous) => ({
                    ...previous,
                    [field.path]: true,
                  }))
                }
                onChangeText={applyDraftText}
                placeholder={
                  field.placeholder ??
                  (field.kind === "date"
                    ? "MM/DD/YYYY"
                    : field.kind === "time"
                      ? "HH:MM (24h)"
                      : field.kind === "money"
                        ? "$"
                        : field.kind === "phone"
                          ? "(555) 555-0100"
                          : "")
                }
                placeholderTextColor={theme.textTertiary}
                style={{
                  backgroundColor: theme.surface,
                  borderColor: editBorderColor,
                  borderRadius: workforceOperationalLayout.controlRadius,
                  borderWidth: errorIssue || warningIssue ? 1.5 : 1,
                  color: theme.textPrimary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: workforceOperationalTypography.body.fontSize,
                  lineHeight: workforceOperationalTypography.body.lineHeight,
                  minHeight: controlMinHeight,
                  paddingHorizontal: 10,
                  paddingVertical: 8,
                }}
                value={draftTexts[field.path] ?? ""}
              />
            )
          ) : (
            <Text
              style={{
                color: valueText == null ? theme.textTertiary : theme.textPrimary,
                fontFamily: layout.bodyFontFamily,
                fontSize: workforceOperationalTypography.body.fontSize,
                fontStyle: valueText == null ? "italic" : "normal",
                fontWeight: valueText == null ? "400" : "600",
                lineHeight: workforceOperationalTypography.body.lineHeight,
              }}
            >
              {valueText ?? "Not captured"}
            </Text>
          )}
          {isEditing && (errorIssue || warningIssue) ? (
            <Text
              accessibilityLiveRegion="polite"
              style={{
                color: errorIssue ? theme.danger : theme.warning,
                fontFamily: layout.bodyFontFamily,
                fontSize: workforceOperationalTypography.metadata.fontSize,
                fontWeight: "600",
                lineHeight: workforceOperationalTypography.metadata.lineHeight,
              }}
            >
              {`${errorIssue ? "Error" : "Warning"}: ${(errorIssue ?? warningIssue)!.message}`}
            </Text>
          ) : null}
          {isEditing && normalizedEcho != null && !errorIssue && !warningIssue ? (
            <Text
              accessibilityLiveRegion="polite"
              style={{
                color: theme.textSecondary,
                fontFamily: layout.bodyFontFamily,
                fontSize: workforceOperationalTypography.metadata.fontSize,
                lineHeight: workforceOperationalTypography.metadata.lineHeight,
              }}
            >
              {`Normalized: ${normalizedEcho}`}
            </Text>
          ) : null}
          {!isEditing ? renderProvenanceChip(field) : null}
        </View>
      </View>
    );
  };

  const renderCardHeader = (input: {
    actions: ReactNode;
    badge?: ReactNode;
    iconName: LineIconName;
    subtitle?: string | null;
    title: string;
  }) => (
    <View
      style={{
        alignItems: isCompact ? "stretch" : "center",
        backgroundColor: theme.backgroundSubtle,
        borderBottomColor: theme.borderSubtle,
        borderBottomWidth: 1,
        flexDirection: isCompact ? "column" : "row",
        gap: 12,
        justifyContent: "space-between",
        paddingHorizontal: workforceConfigurationDialog.paddingCompact,
        paddingVertical: 12,
      }}
    >
      <View
        style={{
          alignItems: "center",
          flexDirection: "row",
          flexShrink: 1,
          gap: 10,
          minWidth: 0,
        }}
      >
        <View
          style={{
            alignItems: "center",
            backgroundColor: theme.accentSubtle,
            borderRadius: workforceOperationalLayout.controlRadius,
            height: 32,
            justifyContent: "center",
            width: 32,
          }}
        >
          <LineIcon
            color={theme.accentStrong}
            name={input.iconName}
            size={17}
            strokeWidth={2.35}
          />
        </View>
        <View style={{ flexShrink: 1, gap: 2, minWidth: 0 }}>
          <View
            style={{
              alignItems: "center",
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 8,
            }}
          >
            <Text
              style={{
                color: theme.textPrimary,
                fontFamily: layout.displayFontFamily,
                fontSize: workforceOperationalTypography.sectionTitle.fontSize,
                fontWeight: "800",
                letterSpacing: 0,
                lineHeight: workforceOperationalTypography.sectionTitle.lineHeight,
              }}
            >
              {input.title}
            </Text>
            {input.badge}
          </View>
          {input.subtitle ? (
            <Text
              style={{
                color: theme.textSecondary,
                fontFamily: layout.bodyFontFamily,
                fontSize: workforceOperationalTypography.metadata.fontSize,
                lineHeight: workforceOperationalTypography.metadata.lineHeight,
              }}
            >
              {input.subtitle}
            </Text>
          ) : null}
        </View>
      </View>
      <View
        style={{
          flexDirection: "row",
          flexShrink: 0,
          flexWrap: "wrap",
          gap: 8,
          justifyContent: isCompact ? "flex-start" : "flex-end",
        }}
      >
        {input.actions}
      </View>
    </View>
  );

  const renderSectionCard = (section: SectionDescriptor) => {
    const sectionReadiness = readiness.find(
      (candidate) => candidate.id === section.id,
    );
    const isEditing = editingSectionId === section.id;
    const sectionErrorCount = isEditing
      ? (draftValidation?.errors.length ?? 0)
      : 0;
    const sectionWarnings = isEditing ? (draftValidation?.warnings ?? []) : [];
    const isComplete =
      sectionReadiness != null &&
      sectionReadiness.filledCount === sectionReadiness.totalCount;
    const sectionGroups = fieldGroupsBySection[section.id]
      .map((group) => ({
        ...group,
        fields: section.fields.filter(
          (field) => resolveFieldGroupId(section.id, field.path) === group.id,
        ),
      }))
      .filter((group) => group.fields.length > 0);

    return (
      <View
        key={section.id}
        nativeID={`clinician-profile-section-${section.id}`}
        onLayout={(event) => {
          sectionOffsets.current[section.id] = event.nativeEvent.layout.y;
        }}
        style={{
          backgroundColor: theme.surface,
          borderColor: theme.borderStrong,
          borderRadius: workforceOperationalLayout.panelRadius,
          borderWidth: 1,
          overflow: "hidden",
        }}
      >
        {renderCardHeader({
          actions: isEditing
            ? usesInlineEditActions
              ? renderEditingActions(section)
              : null
            : (
                <>
                  {renderActionPill({
                    accessibilityLabel: `Edit ${section.title}`,
                    disabled:
                      isWorkbenchActionSubmitting || editingSectionId != null,
                    iconName: "edit",
                    label: "Edit",
                    onPress: () => beginEditingSection(section),
                  })}
                  {renderActionPill({
                    accessibilityLabel: `Copy ${section.title}`,
                    iconName: "document",
                    label: copiedSectionId === section.id ? "Copied" : "Copy",
                    onPress: () => copySectionText(section),
                    tone: "neutral",
                  })}
                </>
              ),
          badge: sectionReadiness ? (
            <View
              style={{
                backgroundColor: isComplete
                  ? theme.statusChip.success.background
                  : theme.statusChip.warning.background,
                borderRadius: 999,
                paddingHorizontal: 8,
                paddingVertical: 3,
              }}
            >
              <Text
                style={{
                  color: isComplete
                    ? theme.statusChip.success.text
                    : theme.statusChip.warning.text,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: workforceOperationalTypography.metadata.fontSize,
                  fontWeight: "700",
                  lineHeight: workforceOperationalTypography.metadata.lineHeight,
                }}
              >
                {isComplete
                  ? "Complete"
                  : `${sectionReadiness.filledCount}/${sectionReadiness.totalCount}`}
              </Text>
            </View>
          ) : undefined,
          iconName: sectionIconNames[section.id],
          subtitle: isEditing
            ? `${String(changedFieldCount)} changed · ${String(sectionErrorCount)} error${
                  sectionErrorCount === 1 ? "" : "s"
                }`
            : sectionReadiness && !isComplete
              ? `${sectionReadiness.totalCount - sectionReadiness.filledCount} field${
                  sectionReadiness.totalCount - sectionReadiness.filledCount === 1
                    ? ""
                    : "s"
                } still to capture`
              : null,
          title: section.title,
        })}
        {isEditing && sectionWarnings.length > 0 ? (
          <View
            accessibilityLiveRegion="polite"
            style={{
              backgroundColor: theme.statusChip.warning.background,
              borderBottomColor: theme.statusChip.warning.border,
              borderBottomWidth: 1,
              paddingHorizontal: workforceConfigurationDialog.paddingCompact,
              paddingVertical: 9,
            }}
          >
            <Text
              style={{
                color: theme.statusChip.warning.text,
                fontFamily: layout.bodyFontFamily,
                fontSize: workforceOperationalTypography.table.fontSize,
                fontWeight: "700",
                lineHeight: workforceOperationalTypography.table.lineHeight,
              }}
            >
              {`${String(sectionWarnings.length)} warning${sectionWarnings.length === 1 ? "" : "s"}. Review before saving.`}
            </Text>
          </View>
        ) : null}
        {sectionGroups.map((group, groupIndex) => (
          <View
            key={group.id}
            style={{
              borderTopColor: theme.borderSubtle,
              borderTopWidth: groupIndex === 0 ? 0 : 1,
              paddingHorizontal: workforceConfigurationDialog.paddingCompact,
              paddingVertical: 14,
            }}
          >
            <View style={{ gap: 2, marginBottom: 8 }}>
              <Text
                style={{
                  color: theme.textPrimary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: workforceOperationalTypography.body.fontSize,
                  fontWeight: "800",
                  lineHeight: workforceOperationalTypography.body.lineHeight,
                }}
              >
                {group.title}
              </Text>
              <Text
                style={{
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: workforceOperationalTypography.metadata.fontSize,
                  lineHeight: workforceOperationalTypography.metadata.lineHeight,
                }}
              >
                {group.description}
              </Text>
            </View>
            <View
              style={{
                flexDirection: "row",
                flexWrap: "wrap",
                gap: workforceConfigurationDialog.fieldGap,
              }}
            >
              {group.fields.map((field) =>
                renderFieldRow(section, group, field),
              )}
            </View>
          </View>
        ))}
        {isEditing && usesInlineEditActions ? (
          <View
            style={{
              alignItems: "stretch",
              backgroundColor: theme.backgroundSubtle,
              borderTopColor: theme.borderSubtle,
              borderTopWidth: 1,
              gap: 8,
              padding: workforceConfigurationDialog.paddingCompact,
            }}
          >
            <Text
              accessibilityLiveRegion="polite"
              style={{
                color:
                  sectionErrorCount > 0
                    ? theme.danger
                    : theme.textSecondary,
                fontFamily: layout.bodyFontFamily,
                fontSize: workforceOperationalTypography.metadata.fontSize,
                lineHeight: workforceOperationalTypography.metadata.lineHeight,
              }}
            >
              {`${String(changedFieldCount)} changed · ${String(sectionErrorCount)} error${
                sectionErrorCount === 1 ? "" : "s"
              } · ${String(sectionWarnings.length)} warning${
                sectionWarnings.length === 1 ? "" : "s"
              }`}
            </Text>
            <View
              style={{
                flexDirection: "row",
                flexWrap: "wrap",
                gap: workforceConfigurationDialog.actionGap,
              }}
            >
              {renderEditingActions(section)}
            </View>
          </View>
        ) : null}
      </View>
    );
  };

  const profileNavigationItems: {
    accessibilityStatus: string;
    id: ClinicianPatientProfileFocusTarget;
    label: string;
    status: string;
  }[] = [
    ...profileSections.map((section) => {
      const sectionReadiness = readiness.find(
        (candidate) => candidate.id === section.id,
      );

      return {
        accessibilityStatus: sectionReadiness
          ? `${String(sectionReadiness.filledCount)} of ${String(sectionReadiness.totalCount)} fields complete`
          : "0 of 0 fields complete",
        id: section.id,
        label: section.title,
        status: sectionReadiness
          ? `${String(sectionReadiness.filledCount)}/${String(sectionReadiness.totalCount)}`
          : "0/0",
      };
    }),
    {
      accessibilityStatus: `${String(chartPackDocuments.length)} documents`,
      id: "chart-pack",
      label: "Chart pack",
      status: `${String(chartPackDocuments.length)} docs`,
    },
  ];

  useEffect(() => {
    if (
      typeof document === "undefined" ||
      typeof window === "undefined" ||
      !usesInlineEditActions
    ) {
      return;
    }

    const firstSection = document.getElementById(
      "clinician-profile-section-patient",
    );
    const scrollParent = firstSection
      ? findWebScrollAncestor(firstSection)
      : null;

    if (!scrollParent) {
      return;
    }

    let animationFrame: number | null = null;
    const updateActiveSection = () => {
      animationFrame = null;
      const parentTop = scrollParent.getBoundingClientRect().top;
      const activeThreshold =
        Math.max(0, parentTop) + stickyHeaderHeight.current + 24;
      let nextSectionId: ClinicianPatientProfileFocusTarget = "patient";

      for (const targetId of profileNavigationTargetIds) {
        const targetElement = document.getElementById(
          `clinician-profile-section-${targetId}`,
        );

        if (
          targetElement &&
          targetElement.getBoundingClientRect().top <= activeThreshold
        ) {
          nextSectionId = targetId;
        }
      }

      setActiveSectionId((previous) =>
        previous === nextSectionId ? previous : nextSectionId,
      );
    };
    const scheduleActiveSectionUpdate = () => {
      if (animationFrame == null) {
        animationFrame = window.requestAnimationFrame(updateActiveSection);
      }
    };

    scrollParent.addEventListener("scroll", scheduleActiveSectionUpdate, {
      passive: true,
    });
    window.addEventListener("resize", scheduleActiveSectionUpdate);
    scheduleActiveSectionUpdate();

    return () => {
      scrollParent.removeEventListener("scroll", scheduleActiveSectionUpdate);
      window.removeEventListener("resize", scheduleActiveSectionUpdate);
      if (animationFrame != null) {
        window.cancelAnimationFrame(animationFrame);
      }
    };
  }, [patientId, usesInlineEditActions]);

  useEffect(() => {
    revealProfileNavigationTarget(activeSectionId, false);
  }, [activeSectionId, revealProfileNavigationTarget]);

  const renderProfileNavigationItems = () =>
    profileNavigationItems.map((navigationItem) => {
      const isActive = activeSectionId === navigationItem.id;

      return (
        <Pressable
          accessibilityLabel={`${navigationItem.label}, ${navigationItem.accessibilityStatus}`}
          accessibilityRole="button"
          accessibilityState={{ selected: isActive }}
          aria-pressed={isActive}
          key={navigationItem.id}
          onLayout={(event) => {
            navigationOffsets.current[navigationItem.id] = {
              width: event.nativeEvent.layout.width,
              x: event.nativeEvent.layout.x,
            };
          }}
          onPress={() => scrollToProfileSection(navigationItem.id)}
          style={({ pressed }) => ({
            alignItems: "center",
            backgroundColor: isActive
              ? theme.accentSubtle
              : pressed
                ? theme.backgroundSubtle
                : theme.surface,
            borderColor: isActive
              ? theme.accent
              : theme.borderSubtle,
            borderRadius: workforceOperationalLayout.controlRadius,
            borderWidth: 1,
            flexBasis: isCompact
              ? "47%"
              : usesInlineEditActions
                ? "auto"
                : "31%",
            flexDirection: "row",
            flexGrow: isCompact || !usesInlineEditActions ? 1 : 0,
            flexShrink: 0,
            gap: 8,
            justifyContent: "center",
            minHeight: controlMinHeight,
            minWidth: 0,
            paddingHorizontal: isCompact ? 10 : 14,
            paddingVertical: 6,
          })}
        >
          <Text
            numberOfLines={isCompact ? 2 : 1}
            style={{
              color: isActive
                ? theme.accentStrong
                : theme.textSecondary,
              fontFamily: layout.bodyFontFamily,
              fontSize: workforceOperationalTypography.table.fontSize,
              fontWeight: "700",
              flexShrink: isCompact ? 1 : 0,
              lineHeight: workforceOperationalTypography.table.lineHeight,
              textAlign: "center",
            }}
          >
            {navigationItem.label}
          </Text>
          <View
            style={{
              backgroundColor: isActive
                ? theme.surface
                : theme.backgroundSubtle,
              borderRadius: 999,
              minWidth: 34,
              paddingHorizontal: 5,
              paddingVertical: 2,
            }}
          >
            <Text
              numberOfLines={1}
              style={{
                color: isActive
                  ? theme.accentStrong
                  : theme.textSecondary,
                fontFamily: layout.bodyFontFamily,
                fontSize: workforceOperationalTypography.metadata.fontSize,
                fontWeight: "700",
                lineHeight: workforceOperationalTypography.metadata.lineHeight,
                textAlign: "center",
              }}
            >
              {navigationItem.status}
            </Text>
          </View>
        </Pressable>
      );
    });

  return (
    <View style={{ flex: 1, position: "relative" }}>
      <ScrollView
        ref={scrollRef}
        contentContainerStyle={{
          gap: 12,
          paddingBottom: editingSection && !usesInlineEditActions
            ? 92
            : 24,
        }}
        onScroll={(event) => {
          const activeThreshold =
            event.nativeEvent.contentOffset.y +
            stickyHeaderHeight.current +
            24;
          let nextSectionId: ClinicianPatientProfileFocusTarget = "patient";

          for (const navigationItem of profileNavigationItems) {
            const offset = sectionOffsets.current[navigationItem.id];

            if (offset != null && offset <= activeThreshold) {
              nextSectionId = navigationItem.id;
            }
          }

          setActiveSectionId((previous) =>
            previous === nextSectionId ? previous : nextSectionId,
          );
        }}
        scrollEventThrottle={64}
        stickyHeaderIndices={[0]}
        style={{
          flex:
            Platform.OS === "web" && usesInlineEditActions
              ? undefined
              : 1,
          overflow:
            Platform.OS === "web" && usesInlineEditActions
              ? "visible"
              : undefined,
        }}
      >
        <View
          onLayout={(event) => {
            stickyHeaderHeight.current = event.nativeEvent.layout.height;
          }}
          style={{
            backgroundColor: theme.surface,
            borderBottomColor: theme.borderSubtle,
            borderBottomWidth: 1,
            gap: 10,
            paddingBottom: 10,
            paddingHorizontal: 2,
            paddingTop: 2,
            zIndex: 10,
          }}
        >
          <View
            style={{
              alignItems: isCompact ? "stretch" : "center",
              flexDirection: isCompact ? "column" : "row",
              gap: 10,
              justifyContent: "space-between",
            }}
          >
            <View style={{ flexShrink: 1, gap: 2 }}>
              <Text
                style={{
                  color: theme.textPrimary,
                  fontFamily: layout.displayFontFamily,
                  fontSize: workforceOperationalTypography.sectionTitle.fontSize,
                  fontWeight: "800",
                  letterSpacing: 0,
                  lineHeight: workforceOperationalTypography.sectionTitle.lineHeight,
                }}
              >
                Patient details
              </Text>
              <Text
                style={{
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: workforceOperationalTypography.metadata.fontSize,
                  lineHeight: workforceOperationalTypography.metadata.lineHeight,
                }}
              >
                Identity, case, contacts, coverage, and chart documents.
              </Text>
            </View>
            {renderActionPill({
              accessibilityLabel: "Copy complete patient profile",
              iconName: "document",
              label: copiedSectionId === "all" ? "Copied" : "Copy profile",
              onPress: copyWholeProfile,
              tone: "neutral",
            })}
          </View>
          {isCompact ? (
            <View
              accessibilityLabel="Patient detail sections"
              style={{
                flexDirection: "row",
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              {renderProfileNavigationItems()}
            </View>
          ) : usesInlineEditActions ? (
            <ScrollView
              accessibilityLabel="Patient detail sections"
              contentContainerStyle={{
                flexDirection: "row",
                gap: 8,
                paddingBottom: 2,
              }}
              horizontal
              onContentSizeChange={() => {
                revealProfileNavigationTarget(activeSectionId, false);
              }}
              onLayout={(event) => {
                navigationViewportWidth.current =
                  event.nativeEvent.layout.width;
                revealProfileNavigationTarget(activeSectionId, false);
              }}
              onScroll={(event) => {
                navigationScrollLeft.current =
                  event.nativeEvent.contentOffset.x;
              }}
              ref={navigationScrollRef}
              scrollEventThrottle={32}
              showsHorizontalScrollIndicator
            >
              {renderProfileNavigationItems()}
            </ScrollView>
          ) : (
            <View
              accessibilityLabel="Patient detail sections"
              style={{
                flexDirection: "row",
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              {renderProfileNavigationItems()}
            </View>
          )}
          {utilityFeedback != null ||
          (hasSubmittedPatientRecordAction && actionFeedbackText) ? (
            <View
              accessibilityLiveRegion="polite"
              accessibilityRole={
                utilityFeedback?.tone === "error" ||
                actionFeedbackTone === "error"
                  ? "alert"
                  : undefined
              }
              style={{
                backgroundColor:
                  (utilityFeedback?.tone ?? actionFeedbackTone) === "error"
                    ? theme.statusChip.danger.background
                    : (utilityFeedback?.tone ?? actionFeedbackTone) ===
                        "success"
                      ? theme.statusChip.success.background
                      : theme.backgroundSubtle,
                borderColor:
                  (utilityFeedback?.tone ?? actionFeedbackTone) === "error"
                    ? theme.statusChip.danger.border
                    : (utilityFeedback?.tone ?? actionFeedbackTone) ===
                        "success"
                      ? theme.statusChip.success.border
                      : theme.borderStrong,
                borderRadius: workforceOperationalLayout.controlRadius,
                borderWidth: 1,
                paddingHorizontal: 10,
                paddingVertical: 8,
              }}
            >
              <Text
                style={{
                  color:
                    (utilityFeedback?.tone ?? actionFeedbackTone) === "error"
                      ? theme.statusChip.danger.text
                      : (utilityFeedback?.tone ?? actionFeedbackTone) ===
                          "success"
                        ? theme.statusChip.success.text
                        : theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: workforceOperationalTypography.table.fontSize,
                  fontWeight: "700",
                  lineHeight: workforceOperationalTypography.table.lineHeight,
                }}
              >
                {utilityFeedback?.text ?? actionFeedbackText}
              </Text>
            </View>
          ) : null}
        </View>
        {renderQuestionnaireStatusCard()}
        {profileSections.map((section) => renderSectionCard(section))}
        {renderChartPackPanelCard()}
        {/* Mounted at tab level, not inside the web-only chart-pack card — the
            status card's "Enter questionnaire" action must work on native too. */}
        {questionnaireDefinition == null ? null : (
          <ClinicianQuestionnaireEntrySurface
            // Snapshot the outbound session when the form opens. A new round
            // starts clean on the next open, while an async refresh cannot
            // remount over answers being typed in the current form.
            key={buildClinicianQuestionnaireEntryKey(
              questionnaireEntryContext.session?.sessionId,
              questionnaireEntryGeneration,
            )}
            visible={showQuestionnaireEntry && !navigationConfirmationOpen}
            patientId={patientId}
            patientDisplayName={patientDisplayName}
            patientIdentityText={patientIdentityText}
            materialChangeToken={materialChangeToken}
            record={displayRecord}
            questions={questionnaireDefinition.questions}
            chartBindings={questionnaireDefinition.chartBindings}
            isSubmitting={isWorkbenchActionSubmitting}
            theme={theme}
            layout={layout}
            onSubmitWorkbenchAction={onSubmitWorkbenchAction}
            onDirtyStateChange={setQuestionnaireDirty}
            onClose={() => {
              setShowQuestionnaireEntry(false);
              setQuestionnaireEntryGeneration((value) => value + 1);
            }}
            outboundSession={questionnaireEntryContext.session}
            outboundAnswerPreview={questionnaireEntryContext.answerPreview}
            onSubmitOutboundManualCommand={onSubmitOutboundManualCommand}
            isOutboundCommandSubmitting={isOutboundCommandSubmitting}
          />
        )}
      </ScrollView>
      {editingSection && !usesInlineEditActions ? (
        <View
          accessibilityLabel={`Editing ${editingSection.title}`}
          style={{
            alignItems: isCompact ? "stretch" : "center",
            backgroundColor: theme.surface,
            borderColor: theme.borderStrong,
            borderRadius: isCompact
              ? 0
              : workforceOperationalLayout.panelRadius,
            borderWidth: 1,
            bottom: 0,
            flexDirection: isCompact ? "column" : "row",
            gap: 12,
            justifyContent: "space-between",
            left: 0,
            padding: 12,
            position: "absolute",
            right: 0,
            zIndex: 20,
          }}
        >
          <View style={{ flex: 1, gap: 2, minWidth: 0 }}>
            <Text
              style={{
                color: theme.textPrimary,
                fontFamily: layout.bodyFontFamily,
                fontSize: workforceOperationalTypography.body.fontSize,
                fontWeight: "800",
                lineHeight: workforceOperationalTypography.body.lineHeight,
              }}
            >
              {`Editing ${editingSection.title}`}
            </Text>
            <Text
              accessibilityLiveRegion="polite"
              style={{
                color:
                  (draftValidation?.errors.length ?? 0) > 0
                    ? theme.danger
                    : theme.textSecondary,
                fontFamily: layout.bodyFontFamily,
                fontSize: workforceOperationalTypography.metadata.fontSize,
                lineHeight: workforceOperationalTypography.metadata.lineHeight,
              }}
            >
              {`${String(changedFieldCount)} changed · ${String(draftValidation?.errors.length ?? 0)} error${
                (draftValidation?.errors.length ?? 0) === 1 ? "" : "s"
              } · ${String(draftValidation?.warnings.length ?? 0)} warning${
                (draftValidation?.warnings.length ?? 0) === 1 ? "" : "s"
              }`}
            </Text>
          </View>
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              gap: workforceConfigurationDialog.actionGap,
              justifyContent: "flex-end",
            }}
          >
            {renderEditingActions(editingSection)}
          </View>
        </View>
      ) : null}
      <Modal
        accessibilityLabel="Discard patient detail changes"
        accessibilityViewIsModal={true}
        animationType="fade"
        onRequestClose={() => setShowProfileDiscardConfirmation(false)}
        transparent={true}
        visible={
          showProfileDiscardConfirmation && !navigationConfirmationOpen
        }
      >
        <View
          style={{
            alignItems: "center",
            backgroundColor: theme.backdropOverlay,
            flex: 1,
            justifyContent: "center",
            padding: 16,
          }}
        >
          <View
            accessibilityLabel="Discard patient detail changes"
            style={{
              backgroundColor: theme.surface,
              borderColor: theme.borderSubtle,
              borderRadius: workforceOperationalLayout.panelRadius,
              borderWidth: 1,
              gap: workforceConfigurationDialog.fieldGap,
              maxWidth: 440,
              padding: workforceConfigurationDialog.paddingWide,
              width: "100%",
            }}
          >
            <View
              style={{
                alignItems: "center",
                flexDirection: "row",
                gap: 8,
              }}
            >
              <LineIcon
                color={theme.warning}
                name="info"
                size={20}
                strokeWidth={2.3}
              />
              <Text
                style={{
                  color: theme.textPrimary,
                  flex: 1,
                  fontFamily: layout.displayFontFamily,
                  fontSize:
                    workforceOperationalTypography.sectionTitle.fontSize,
                  fontWeight: "700",
                  lineHeight:
                    workforceOperationalTypography.sectionTitle.lineHeight,
                }}
              >
                Discard patient detail changes?
              </Text>
            </View>
            <Text
              style={{
                color: theme.textSecondary,
                fontFamily: layout.bodyFontFamily,
                fontSize: workforceOperationalTypography.body.fontSize,
                lineHeight: workforceOperationalTypography.body.lineHeight,
              }}
            >
              Your edits have not been saved. Leaving this form now discards
              them.
            </Text>
            <View
              style={{
                flexDirection: "row",
                flexWrap: "wrap",
                gap: workforceConfigurationDialog.actionGap,
                justifyContent: "flex-end",
              }}
            >
              <Pressable
                accessibilityLabel="Stay in patient details"
                accessibilityRole="button"
                onPress={() => setShowProfileDiscardConfirmation(false)}
                ref={profileDiscardStayButtonRef}
                style={{
                  alignItems: "center",
                  borderColor: theme.borderStrong,
                  borderRadius: workforceOperationalLayout.controlRadius,
                  borderWidth: 1,
                  flexGrow: isCompact ? 1 : 0,
                  justifyContent: "center",
                  minHeight: controlMinHeight,
                  minWidth: 110,
                  paddingHorizontal: 14,
                }}
              >
                <Text
                  style={{
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: workforceOperationalTypography.body.fontSize,
                    fontWeight: "700",
                  }}
                >
                  Stay
                </Text>
              </Pressable>
              <Pressable
                accessibilityLabel="Discard patient detail changes"
                accessibilityRole="button"
                onPress={discardEditingSection}
                style={{
                  alignItems: "center",
                  backgroundColor: theme.danger,
                  borderRadius: workforceOperationalLayout.controlRadius,
                  flexGrow: isCompact ? 1 : 0,
                  justifyContent: "center",
                  minHeight: controlMinHeight,
                  minWidth: 168,
                  paddingHorizontal: 14,
                }}
              >
                <Text
                  style={{
                    color: theme.surface,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: workforceOperationalTypography.body.fontSize,
                    fontWeight: "700",
                  }}
                >
                  Discard changes
                </Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );

  function renderQuestionnaireStatusCard() {
    if (questionnaireDefinition == null || questionnaireStatus == null) {
      return null;
    }

    const { answeredCount, visibleCount, state, latestSource, latestUpdatedAt } =
      questionnaireStatus;
    const activeCallCapturedCount = Math.max(
      outboundSession?.answeredQuestionCount ?? 0,
      outboundAnswerPreview?.length ?? 0,
    );
    const normalizedOutboundStatus =
      outboundSessionStatus?.trim().toLowerCase() ?? "";
    const hasOutboundCallState =
      normalizedOutboundStatus.length > 0 &&
      normalizedOutboundStatus !== "not_started";
    const hasActiveCallSession = outboundSession != null;
    const displayState = hasActiveCallSession ? "in-progress" : state;
    const chipTone =
      normalizedOutboundStatus === "failed" ||
      normalizedOutboundStatus === "cancelled"
        ? theme.statusChip.danger
        : normalizedOutboundStatus === "review_required"
          ? theme.statusChip.warning
          : normalizedOutboundStatus === "scheduled"
            ? theme.statusChip.info
            : displayState === "complete" ||
                normalizedOutboundStatus === "completed"
        ? theme.statusChip.success
              : displayState === "in-progress"
                ? theme.statusChip.warning
                : null;
    const stateLabel =
      hasOutboundCallState
        ? formatClinicianOutboundSessionStatus(outboundSessionStatus)
        : displayState === "complete"
        ? "Complete"
        : displayState === "in-progress"
          ? "In progress"
          : "Not started";
    const capturedVia =
      latestSource === "questionnaire"
        ? " via AI call"
        : latestSource === "manual"
          ? " via manual entry"
          : latestSource
            ? ` via ${sourceLabels[latestSource] ?? latestSource}`
            : "";
    const activeCallProgressText = hasActiveCallSession
      ? activeCallCapturedCount > 0
        ? `${activeCallCapturedCount}${
            (outboundSession?.totalQuestionCount ?? 0) > 0
              ? ` of ${outboundSession?.totalQuestionCount}`
              : ""
          } answers captured in the active call`
        : "Active call has not captured an answer yet"
      : null;
    const recordProgressText =
      state === "not-started"
        ? "No answers on file — enter manually or run the AI call."
        : `${answeredCount} of ${visibleCount} applicable answers on file` +
          (latestUpdatedAt
            ? ` · updated ${formatProvenanceTimestamp(latestUpdatedAt)}${capturedVia}`
            : "");
    const subtitle =
      activeCallProgressText == null
        ? recordProgressText
        : answeredCount > 0
          ? `${recordProgressText} · ${activeCallProgressText}`
          : `${activeCallProgressText} · open to review and save`;

    return (
      <View
        style={{
          backgroundColor: theme.surface,
          borderColor: theme.borderStrong,
          borderRadius: workforceOperationalLayout.panelRadius,
          borderWidth: 1,
          overflow: "hidden",
        }}
      >
        {renderCardHeader({
          actions: renderActionPill({
            accessibilityHint: isQuestionnaireLockedByLiveCall
              ? "The questionnaire unlocks when the AI call finishes."
              : undefined,
            accessibilityLabel: "Open patient questionnaire",
            disabled:
              isQuestionnaireLockedByLiveCall ||
              isOutboundSessionLoading ||
              editingSectionId != null,
            emphasis: "solid",
            iconName: isQuestionnaireLockedByLiveCall ? "phone" : "edit",
            label: isQuestionnaireLockedByLiveCall
              ? "Locked — call in progress"
              : isOutboundSessionLoading
                ? "Loading session"
                : "Enter questionnaire",
            onPress: openQuestionnaireEntry,
            ...(isQuestionnaireLockedByLiveCall
              ? { tone: "warning" as const }
              : {}),
          }),
          badge: (
            <View
              style={{
                backgroundColor: chipTone
                  ? chipTone.background
                  : theme.backgroundSubtle,
                borderColor: chipTone ? chipTone.border : theme.borderStrong,
                borderRadius: 999,
                borderWidth: 1,
                paddingHorizontal: 8,
                paddingVertical: 3,
              }}
            >
              <Text
                style={{
                  color: chipTone ? chipTone.text : theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: workforceOperationalTypography.metadata.fontSize,
                  fontWeight: "700",
                  lineHeight: workforceOperationalTypography.metadata.lineHeight,
                }}
              >
                {stateLabel}
              </Text>
            </View>
          ),
          iconName: "edit",
          subtitle,
          title: "Questionnaire",
        })}
      </View>
    );
  }

  function printChartPackHtml(html: string): void {
    if (Platform.OS !== "web" || typeof window === "undefined") {
      setUtilityFeedback({
        text: "Printing is unavailable on this device.",
        tone: "error",
      });
      return;
    }

    try {
      const printWindow = window.open("", "_blank");

      if (!printWindow) {
        setUtilityFeedback({
          text: "The print window was blocked. Allow pop-ups and try again.",
          tone: "error",
        });
        return;
      }

      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      setUtilityFeedback({
        text: "Print preview opened in a new window.",
        tone: "success",
      });
    } catch {
      setUtilityFeedback({
        text: "Print preview could not be opened. Try again.",
        tone: "error",
      });
    }
  }

  function renderChartPackPreviewOverlay() {
    if (previewTarget == null || navigationConfirmationOpen) {
      return null;
    }

    const isPack = previewTarget.kind === "pack";
    const definition = isPack
      ? null
      : chartPackDocuments[previewTarget.index] ?? null;
    const titleText = isPack
      ? "Full chart pack"
      : (definition?.title ?? "Document");
    const previewHtml = isPack
      ? renderFullChartPackHtml(displayRecord, chartPackQuestionnaireContent)
      : definition != null
        ? renderChartPackDocumentHtml(
            displayRecord,
            definition,
            chartPackQuestionnaireContent,
          )
        : "";
    const documentIndex = isPack ? 0 : previewTarget.index;

    return (
      <Modal
        accessibilityLabel={titleText}
        accessibilityViewIsModal
        animationType="fade"
        onRequestClose={() => setPreviewTarget(null)}
        transparent
        visible
      >
        <View
          style={{
            backgroundColor: "rgba(15, 23, 42, 0.82)",
            flex: 1,
            padding: isCompact ? 0 : workforceConfigurationDialog.paddingWide,
          }}
        >
          <View
            style={{
              backgroundColor: theme.surface,
              borderRadius: isCompact
                ? 0
                : workforceOperationalLayout.panelRadius,
              flex: 1,
              overflow: "hidden",
            }}
          >
            <View
              style={{
                alignItems: isCompact ? "stretch" : "center",
                backgroundColor: theme.backgroundSubtle,
                borderBottomColor: theme.borderSubtle,
                borderBottomWidth: 1,
                flexDirection: isCompact ? "column" : "row",
                gap: 12,
                justifyContent: "space-between",
                paddingHorizontal: workforceConfigurationDialog.paddingCompact,
                paddingVertical: 12,
              }}
            >
              <View
                style={{
                  alignItems: "center",
                  flexDirection: "row",
                  flexShrink: 1,
                  flexWrap: "wrap",
                  gap: 10,
                  minWidth: 0,
                }}
              >
                <Text
                  style={{
                    color: theme.textPrimary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: workforceOperationalTypography.sectionTitle.fontSize,
                    fontWeight: "800",
                    lineHeight: workforceOperationalTypography.sectionTitle.lineHeight,
                  }}
                >
                  {titleText}
                </Text>
                {!isPack ? (
                  <Text
                    style={{
                      color: theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: workforceOperationalTypography.metadata.fontSize,
                      fontWeight: "600",
                      lineHeight: workforceOperationalTypography.metadata.lineHeight,
                    }}
                  >
                    {`Document ${String(documentIndex + 1)} of ${String(chartPackDocuments.length)}`}
                  </Text>
                ) : null}
              </View>
              <View
                style={{
                  flexDirection: "row",
                  flexShrink: 0,
                  flexWrap: "wrap",
                  gap: 8,
                }}
              >
                {!isPack
                  ? renderActionPill({
                      accessibilityLabel: "Preview previous chart-pack document",
                      disabled: documentIndex <= 0,
                      key: "preview-prev",
                      label: "Previous",
                      onPress: () =>
                        setPreviewTarget({
                          kind: "doc",
                          index: documentIndex - 1,
                        }),
                      tone: "neutral",
                    })
                  : null}
                {!isPack
                  ? renderActionPill({
                      accessibilityLabel: "Preview next chart-pack document",
                      disabled:
                        documentIndex >= chartPackDocuments.length - 1,
                      iconName: "chevron-right",
                      key: "preview-next",
                      label: "Next",
                      onPress: () =>
                        setPreviewTarget({
                          kind: "doc",
                          index: documentIndex + 1,
                        }),
                      tone: "neutral",
                    })
                  : null}
                {renderActionPill({
                  accessibilityLabel: `Print ${titleText}`,
                  emphasis: "solid",
                  iconName: "document",
                  key: "preview-print",
                  label: "Print",
                  onPress: () => printChartPackHtml(previewHtml),
                })}
                {renderActionPill({
                  accessibilityLabel: "Close chart-pack preview",
                  iconName: "x",
                  key: "preview-close",
                  label: "Close",
                  onPress: () => setPreviewTarget(null),
                  tone: "neutral",
                })}
              </View>
            </View>
            {utilityFeedback ? (
              <View
                accessibilityLiveRegion="polite"
                accessibilityRole={
                  utilityFeedback.tone === "error" ? "alert" : undefined
                }
                style={{
                  backgroundColor:
                    utilityFeedback.tone === "error"
                      ? theme.statusChip.danger.background
                      : theme.statusChip.success.background,
                  borderBottomColor:
                    utilityFeedback.tone === "error"
                      ? theme.statusChip.danger.border
                      : theme.statusChip.success.border,
                  borderBottomWidth: 1,
                  paddingHorizontal:
                    workforceConfigurationDialog.paddingCompact,
                  paddingVertical: 8,
                }}
              >
                <Text
                  style={{
                    color:
                      utilityFeedback.tone === "error"
                        ? theme.statusChip.danger.text
                        : theme.statusChip.success.text,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: workforceOperationalTypography.table.fontSize,
                    fontWeight: "700",
                    lineHeight: workforceOperationalTypography.table.lineHeight,
                  }}
                >
                  {utilityFeedback.text}
                </Text>
              </View>
            ) : null}
            {Platform.OS === "web" ? (
              <iframe
                sandbox=""
                srcDoc={previewHtml}
                style={{ border: "none", flex: 1, width: "100%" }}
                title={titleText}
              />
            ) : null}
          </View>
        </View>
      </Modal>
    );
  }

  function renderChartPackPanelCard() {
    return (
      <View
        nativeID="clinician-profile-section-chart-pack"
        onLayout={(event) => {
          sectionOffsets.current["chart-pack"] = event.nativeEvent.layout.y;
        }}
        style={{
          backgroundColor: theme.surface,
          borderColor: theme.borderStrong,
          borderRadius: workforceOperationalLayout.panelRadius,
          borderWidth: 1,
          overflow: "hidden",
        }}
      >
        {renderCardHeader({
          actions: (
            <>
              {renderActionPill({
                accessibilityLabel: "Preview full chart pack",
                disabled: editingSectionId != null,
                iconName: "eye",
                label: "Preview full pack",
                onPress: () => setPreviewTarget({ kind: "pack" }),
                tone: "neutral",
              })}
              {renderActionPill({
                accessibilityLabel: "Print full chart pack",
                disabled: editingSectionId != null,
                emphasis: "solid",
                iconName: "document",
                label: "Print full pack",
                onPress: () =>
                  printChartPackHtml(
                    renderFullChartPackHtml(
                      displayRecord,
                      chartPackQuestionnaireContent,
                    ),
                  ),
              })}
            </>
          ),
          iconName: "document",
          subtitle:
            "Printable day-of-surgery documents populated from this record. Missing fields print as blanks.",
          title: "Chart pack",
        })}
        <View
          style={{
            paddingHorizontal: workforceConfigurationDialog.paddingCompact,
            paddingVertical: 4,
          }}
        >
        {chartPackDocuments.map((definition, definitionIndex) => {
          const gaps = computeChartPackDocumentGaps(record, definition);
          const isReady = gaps.length === 0;

          return (
            <View
              key={definition.id}
              style={{
                alignItems: isCompact ? "stretch" : "center",
                borderBottomColor: theme.borderSubtle,
                borderBottomWidth: 1,
                flexDirection: isCompact ? "column" : "row",
                gap: 10,
                justifyContent: "space-between",
                minHeight: workforceOperationalLayout.rowMinHeight,
                paddingVertical: 9,
              }}
            >
              <View
                style={{
                  flex: 1,
                  flexShrink: 1,
                  gap: 1,
                  minWidth: 0,
                }}
              >
                <Text
                  style={{
                    color: theme.textPrimary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: workforceOperationalTypography.body.fontSize,
                    fontWeight: "700",
                    lineHeight: workforceOperationalTypography.body.lineHeight,
                  }}
                >
                  {definition.title}
                </Text>
                <Text
                  style={{
                    color: isReady ? theme.success : theme.warning,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: workforceOperationalTypography.metadata.fontSize,
                    fontWeight: "700",
                    lineHeight: workforceOperationalTypography.metadata.lineHeight,
                  }}
                >
                  {isReady
                    ? "Ready"
                    : `${String(gaps.length)} field${gaps.length === 1 ? "" : "s"} missing`}
                </Text>
              </View>
              <View
                style={{
                  flexDirection: "row",
                  flexShrink: 0,
                  flexWrap: "wrap",
                  gap: 8,
                }}
              >
                {renderActionPill({
                  accessibilityLabel: `Preview ${definition.title}`,
                  disabled: editingSectionId != null,
                  iconName: "eye",
                  key: `preview-${definition.id}`,
                  label: "Preview",
                  onPress: () =>
                    setPreviewTarget({ kind: "doc", index: definitionIndex }),
                  tone: "neutral",
                })}
                {renderActionPill({
                  accessibilityLabel: `Print ${definition.title}`,
                  disabled: editingSectionId != null,
                  iconName: "document",
                  key: `print-${definition.id}`,
                  label: "Print",
                  onPress: () =>
                    printChartPackHtml(
                      renderChartPackDocumentHtml(
                        displayRecord,
                        definition,
                        chartPackQuestionnaireContent,
                      ),
                    ),
                })}
              </View>
            </View>
          );
        })}
        </View>
        {renderChartPackPreviewOverlay()}
      </View>
    );
  }
}

export { profileSections };
export type { FieldDescriptor, SectionDescriptor };
